import React, { useState } from "react";

import { Form, Formik, FormikProps, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { RuleSetAction } from "actions/ruleSets";

import { Button, SecondaryButton } from "components/Form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { RuleSetTable } from "components/RuleSets/RuleSetTable";
import { RuleSetSummaryForm } from "components/RuleSets/RuleSetTable/RuleSetSummaryForm";

import { ModalTypes } from "constants/navigation";
import { RuleSetPermissions } from "constants/permissions";

import {
  convertToExternalCriterion,
  convertToExternalInputField,
} from "containers/Settings/RuleBooks/export";
import { convertToInternalRuleSet } from "containers/Settings/RuleBooks/import";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";

import { hasPermission } from "lib/permissions";

import {
  getContextByModalType,
  getRuleBookIdByRuleId,
  getRuleSetById,
} from "selectors";

import { useBoolean, useIsMobile } from "hooks";
import { useSubmitHandler } from "components/Form/FormikControls";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Please enter a Rule Set name"),
});

interface RuleSetModalProps {
  onClose: () => void;
}

interface RuleSetModalFormProps {
  isCreate: boolean;
  ruleSetId: string | null;
  openEditSettings: () => void;
  isManagedRule: boolean;
  isEditSettingsOpen: boolean;
  handleClose: (formikProps: FormikProps<FormValues>) => void;
  useBasicForm: boolean;
}

interface RuleSetModalContext {
  ruleSetId: string | null;
  newRuleSet: object | null;
  ruleId: string | null;
  isManagedRule: boolean;
  useBasicForm: boolean;
}

interface FormValues {
  criteria: object[];
  id: string | null;
  input_field: object;
  name: string;
  rule_book_id: string;
}

const RuleSetModalForm = ({
  isCreate,
  ruleSetId,
  openEditSettings,
  isManagedRule,
  isEditSettingsOpen,
  handleClose,
  useBasicForm,
}: RuleSetModalFormProps) => {
  const formikProps: FormikProps<FormValues> = useFormikContext();

  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  useSubmitHandler(isSubmitEnabled, setIsSubmitEnabled);

  return (
    <Form>
      <DialogContent dividers>
        {isCreate || useBasicForm ? (
          <RuleSetSummaryForm isManagedRule={false} />
        ) : (
          <RuleSetTable
            ruleSetId={ruleSetId}
            isEditSettingsOpen={isEditSettingsOpen}
            openEditSettings={openEditSettings}
            isManagedRule={isManagedRule}
          />
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={() => handleClose(formikProps)} type="button">
          {isEditSettingsOpen ? "Cancel" : "Back"}
        </SecondaryButton>
        <Button
          data-tour={isCreate ? "create" : "save"}
          type="submit"
          disabled={
            !isSubmitEnabled ||
            (!isEditSettingsOpen && !isCreate && !useBasicForm)
          }
        >
          {isCreate ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Form>
  );
};

export function RuleSetModal(props: RuleSetModalProps) {
  const { onClose }: RuleSetModalProps = props;

  const context =
    useSelector(getContextByModalType(ModalTypes.EditRuleSet)) || {};
  const {
    ruleSetId = null,
    newRuleSet,
    ruleId,
    isManagedRule,
    useBasicForm = false,
  }: RuleSetModalContext = context;

  const isCreate = !ruleSetId;
  const ruleSet = useSelector(getRuleSetById(ruleSetId)) || newRuleSet || {};

  const ruleBookId = useSelector(getRuleBookIdByRuleId(ruleId));

  const readOnly = isCreate
    ? false
    : !hasPermission(ruleSet, RuleSetPermissions.update);

  let preTitle = "Edit";
  if (readOnly) {
    preTitle = "View";
  } else if (isCreate) {
    preTitle = "Create";
  }

  const title = `${preTitle} Rule Set`;

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [isEditSettingsOpen, openEditSettings, closeEditSettings] =
    useBoolean();

  const schema = useRuleFieldSchema();

  const initialValues: FormValues = convertToInternalRuleSet(
    { ...ruleSet },
    schema,
  );

  const handleClose = formikProps => {
    if (isEditSettingsOpen) {
      closeEditSettings();
      formikProps.resetForm(initialValues);
    } else {
      onClose();
    }
  };

  function onSubmit(values, formikProps) {
    values.input_field = convertToExternalInputField(
      `$.${values.input_field.value}`,
    );
    values.criteria = values.criteria.map(convertToExternalCriterion);
    if (isCreate) {
      if (ruleId) {
        dispatch(
          RuleSetAction.addRuleToNewRuleSet(
            {
              ...values,
              id: uuidv4(),
              rule_book_id: ruleBookId,
            },
            ruleId,
          ),
        );
      } else {
        RuleSetAction.create({
          ...values,
          id: uuidv4(),
          rule_book_id: ruleBookId,
        });
      }
    } else {
      dispatch(RuleSetAction.update(values));
    }
    handleClose(formikProps);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={(!useBasicForm && !isCreate) || isMobile}
    >
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <RuleSetModalForm
          isCreate={isCreate}
          ruleSetId={ruleSetId}
          openEditSettings={openEditSettings}
          isManagedRule={isManagedRule}
          isEditSettingsOpen={isEditSettingsOpen}
          handleClose={handleClose}
          useBasicForm={useBasicForm}
        />
      </Formik>
    </Dialog>
  );
}
